// src/auth/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import axios from "axios";

const cfg = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID
};

const userPool = new CognitoUserPool(cfg);
const AuthCtx = createContext(null);
const PENDING_USERNAME_KEY = "cognito:pendingUsername";
const PENDING_EMAIL_KEY = "cognito:pendingEmail";

// Make a non-email username that’s unique & valid for Cognito
function deriveUsernameFromEmail(email) {
  const local = String(email).split("@")[0].replace(/[^a-zA-Z0-9_.-]/g, "");
  const suffix = Date.now().toString(36);
  return `${local || "user"}_${suffix}`;
}

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [cUser, setCUser] = useState(null);
  const [profile, setProfile] = useState(null);   // { sub, email, email_verified }
  const [idToken, setIdToken] = useState(null);

  // Attach token to all axios calls
  useEffect(() => {
    if (idToken) axios.defaults.headers.common.Authorization = `Bearer ${idToken}`;
    else delete axios.defaults.headers.common.Authorization;
  }, [idToken]);

  // Bootstrap from existing session
  useEffect(() => {
    const existing = userPool.getCurrentUser();
    if (!existing) { setLoading(false); return; }
    existing.getSession((err, session) => {
      if (err || !session?.isValid()) { setLoading(false); return; }
      const idTok = session.getIdToken().getJwtToken();
      setIdToken(idTok);
      existing.getUserAttributes((attrErr, attrs) => {
        const obj = {};
        (attrs || []).forEach(a => { obj[a.getName()] = a.getValue(); });
        setProfile({
          sub: obj.sub,
          email: obj.email,
          email_verified: obj.email_verified === "true",
        });
        setCUser(existing);
        setLoading(false);
      });
    });
  }, []);

  // ---- Auth actions ----

  // 1) Sign up with graceful fallback:
  // - Try "email as username" first (works when pool is Email-as-Username)
  // - If Cognito returns the "email format" error, retry with a derived username + email attribute
  const signUp = async ({ email, password }) =>
    new Promise((resolve, reject) => {
      const attributes = [new CognitoUserAttribute({ Name: "email", Value: email })];

      const doSignUp = (usernameToUse) => {
        userPool.signUp(usernameToUse, password, attributes, null, (err, result) => {
          if (err) return reject(err);
          try {
            // Persist what we actually signed up with for confirm step
            localStorage.setItem(PENDING_USERNAME_KEY, usernameToUse);
            localStorage.setItem(PENDING_EMAIL_KEY, email);
          } catch {}
          resolve(result);
        });
      };

      // First attempt: use email as username
      userPool.signUp(email, password, attributes, null, (err) => {
        if (!err) {
          try {
            localStorage.setItem(PENDING_USERNAME_KEY, email);
            localStorage.setItem(PENDING_EMAIL_KEY, email);
          } catch {}
          return resolve({ ok: true });
        }

        const msg = String(err?.message || err);
        // Pool is "username + email alias" → retry with non-email username
        const looksLikeAliasError = /username cannot be of email format/i.test(msg);
        if (looksLikeAliasError) {
          const derived = deriveUsernameFromEmail(email);
          return doSignUp(derived);
        }
        return reject(err);
      });
    });

  // 2) Confirm sign-up (use the actual username we stored; fallback to email if missing)
  const confirmSignUp = async ({ email, code }) =>
    new Promise((resolve, reject) => {
      const storedUsername = localStorage.getItem(PENDING_USERNAME_KEY) || email;
      const user = new CognitoUser({ Username: storedUsername, Pool: userPool });
      user.confirmRegistration(code, true, (err, res) => {
        if (err) {
          // Fallback attempt if the stored username doesn’t work
          if (storedUsername !== email) {
            const alt = new CognitoUser({ Username: email, Pool: userPool });
            return alt.confirmRegistration(code, true, (e2, r2) => {
              if (e2) return reject(e2);
              resolve(r2);
            });
          }
          return reject(err);
        }
        resolve(res);
      });
    });

  // 3) Sign in — use email; if pool uses aliases, this works; if pool is email-as-username, it also works
  const signIn = async ({ email, password }) =>
    new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idTok = session.getIdToken().getJwtToken();
          setIdToken(idTok);
          user.getUserAttributes((attrErr, attrs) => {
            const obj = {};
            (attrs || []).forEach(a => { obj[a.getName()] = a.getValue(); });
            setProfile({
              sub: obj.sub,
              email: obj.email,
              email_verified: obj.email_verified === "true",
            });
            setCUser(user);
            // Clear pending artifacts on success
            try {
              localStorage.removeItem(PENDING_USERNAME_KEY);
              localStorage.removeItem(PENDING_EMAIL_KEY);
            } catch {}
            resolve({ session, user });
          });
        },
        onFailure: (err) => {
          // If someone has to sign in with the original username only (rare), hint gracefully
          reject(err);
        },
        newPasswordRequired: () => reject(new Error("New password required")),
      });
    });

  const signOut = () => {
    try { cUser?.signOut(); } catch {}
    setCUser(null);
    setProfile(null);
    setIdToken(null);
  };

  const value = useMemo(() => ({
    loading,
    user: profile,
    idToken,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
  }), [loading, profile, idToken]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

